import React from 'react'
import { Box, Typography, IconButton, Divider, Tooltip, Select, MenuItem } from '@mui/material'
import { useEditor, EditorContent, JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Link from '@tiptap/extension-link'

// Icons for toolbar
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import LinkIcon from '@mui/icons-material/Link'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import FormatColorTextIcon from '@mui/icons-material/FormatColorText'

import { DashboardTextData, TipTapDocument } from '../../../types/gotypes'
import { AgentMarkdown } from '../../../agent'

/**
 * Convert plain text to TipTap document format
 */
const plainTextToTipTap = (content: string): TipTapDocument => {
  const paragraphs = (content || '').split('\n').map(line => ({
    type: 'paragraph' as const,
    content: line ? [{ type: 'text' as const, text: line }] : []
  }))

  return {
    type: 'doc',
    content: paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph' }]
  }
}

/**
 * Extract plain text from TipTap document
 */
export const extractPlainText = (doc: TipTapDocument | undefined): string => {
  if (!doc || !doc.content) return ''

  const extractFromNode = (node: JSONContent): string => {
    if (node.type === 'text' && node.text) {
      return node.text
    }
    if (node.content) {
      return node.content.map(extractFromNode).join('')
    }
    return ''
  }

  return doc.content
    .map(node => extractFromNode(node as JSONContent))
    .join('\n')
}

/**
 * Get TipTap content from DashboardTextData, converting if necessary
 */
const getTipTapContent = (textData: DashboardTextData | undefined): TipTapDocument => {
  if (!textData) {
    return { type: 'doc', content: [{ type: 'paragraph' }] }
  }

  // Prefer TipTap content if available
  if (textData.tiptap_content) {
    return textData.tiptap_content
  }

  // Convert plain text to TipTap format
  return plainTextToTipTap(textData.content)
}

/**
 * Rich text renderer - displays TipTap content in read-only mode
 */
export const RichTextRenderer: React.FC<{ textData: DashboardTextData | undefined }> = ({ textData }) => {
  if (!textData || (!textData.content && !textData.tiptap_content)) {
    return (
      <Typography variant="body2" color="text.secondary">
        No text content
      </Typography>
    )
  }

  // Plain text without tiptap_content: render as markdown (agent-created dashboards)
  if (textData.content && !textData.tiptap_content) {
    return <AgentMarkdown content={textData.content} />
  }

  // TipTap rich text content: render with TipTap editor
  return <TipTapRenderer textData={textData} />
}

/**
 * TipTap renderer for rich text content created via the dashboard editor
 */
const TipTapRenderer: React.FC<{ textData: DashboardTextData }> = ({ textData }) => {
  const content = getTipTapContent(textData)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Link.configure({ openOnClick: true }),
    ],
    content: content as JSONContent,
    editable: false,
  })

  // Update editor content when textData changes
  React.useEffect(() => {
    if (editor && textData) {
      const newContent = getTipTapContent(textData)
      editor.commands.setContent(newContent as JSONContent)
    }
  }, [editor, textData])

  return (
    <Box
      sx={{
        '& .ProseMirror': {
          outline: 'none',
          '& p': { margin: '0.5em 0' },
          '& h1, & h2, & h3': { margin: '0.75em 0 0.5em' },
          '& ul, & ol': { paddingLeft: '1.5em' },
          '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'grey.300',
            paddingLeft: '1em',
            margin: '0.5em 0',
            fontStyle: 'italic',
          },
          '& a': { color: 'primary.main', textDecoration: 'underline' },
        },
      }}
    >
      <EditorContent editor={editor} />
    </Box>
  )
}

/**
 * Toolbar button component
 */
interface ToolbarButtonProps {
  icon: React.ReactNode
  title: string
  isActive?: boolean
  onClick: () => void
  disabled?: boolean
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ icon, title, isActive, onClick, disabled }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      sx={{
        borderRadius: 1,
        backgroundColor: isActive ? 'action.selected' : 'transparent',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      {icon}
    </IconButton>
  </Tooltip>
)

/**
 * Rich text editor with toolbar
 */
interface RichTextEditorProps {
  textData: DashboardTextData | undefined
  onChange: (textData: DashboardTextData) => void
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ textData, onChange }) => {
  const content = getTipTapContent(textData)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'tiptap-link' },
      }),
    ],
    content: content as JSONContent,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON() as TipTapDocument
      const plainText = extractPlainText(json)
      onChange({
        content: plainText,
        tiptap_content: json,
      })
    },
  })

  if (!editor) {
    return null
  }

  const handleLinkClick = () => {
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('Enter URL:', previousUrl || 'https://')

    if (url === null) {
      return // Cancelled
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          p: 1,
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          backgroundColor: 'grey.50',
        }}
      >
        {/* Text size */}
        <Select
          size="small"
          value={
            editor.isActive('heading', { level: 1 }) ? 'h1'
            : editor.isActive('heading', { level: 2 }) ? 'h2'
            : editor.isActive('heading', { level: 3 }) ? 'h3'
            : 'p'
          }
          onChange={(e) => {
            const val = e.target.value
            if (val === 'p') {
              editor.chain().focus().setParagraph().run()
            } else {
              const level = parseInt(val.replace('h', '')) as 1 | 2 | 3
              editor.chain().focus().toggleHeading({ level }).run()
            }
          }}
          sx={{ fontSize: 12, height: 28, minWidth: 90, mr: 0.5, '& .MuiSelect-select': { py: 0.25 } }}
        >
          <MenuItem value="p" sx={{ fontSize: 12 }}>Normal</MenuItem>
          <MenuItem value="h1" sx={{ fontSize: 12, fontWeight: 700 }}>Heading 1</MenuItem>
          <MenuItem value="h2" sx={{ fontSize: 12, fontWeight: 600 }}>Heading 2</MenuItem>
          <MenuItem value="h3" sx={{ fontSize: 12, fontWeight: 500 }}>Heading 3</MenuItem>
        </Select>

        {/* Text color */}
        <Tooltip title="Text color">
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <IconButton
              size="small"
              component="label"
              sx={{ borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' }, p: 0.5 }}
            >
              <FormatColorTextIcon fontSize="small" />
              <input
                type="color"
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                value={editor.getAttributes('textStyle').color || '#000000'}
                onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              />
            </IconButton>
          </Box>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Text formatting */}
        <ToolbarButton
          icon={<FormatBoldIcon fontSize="small" />}
          title="Bold (Ctrl+B)"
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          icon={<FormatItalicIcon fontSize="small" />}
          title="Italic (Ctrl+I)"
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          icon={<StrikethroughSIcon fontSize="small" />}
          title="Strikethrough"
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Lists */}
        <ToolbarButton
          icon={<FormatListBulletedIcon fontSize="small" />}
          title="Bullet List"
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          icon={<FormatListNumberedIcon fontSize="small" />}
          title="Numbered List"
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Alignment */}
        <ToolbarButton
          icon={<FormatAlignLeftIcon fontSize="small" />}
          title="Align Left"
          isActive={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        />
        <ToolbarButton
          icon={<FormatAlignCenterIcon fontSize="small" />}
          title="Align Center"
          isActive={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        />
        <ToolbarButton
          icon={<FormatAlignRightIcon fontSize="small" />}
          title="Align Right"
          isActive={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Other */}
        <ToolbarButton
          icon={<FormatQuoteIcon fontSize="small" />}
          title="Blockquote"
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <ToolbarButton
          icon={<LinkIcon fontSize="small" />}
          title="Add Link"
          isActive={editor.isActive('link')}
          onClick={handleLinkClick}
        />

        <Box sx={{ flexGrow: 1 }} />

        {/* Undo/Redo */}
        <ToolbarButton
          icon={<UndoIcon fontSize="small" />}
          title="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        />
        <ToolbarButton
          icon={<RedoIcon fontSize="small" />}
          title="Redo (Ctrl+Y)"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        />
      </Box>

      {/* Editor content */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 1,
          minHeight: 150,
          '& .ProseMirror': {
            padding: 2,
            minHeight: 150,
            outline: 'none',
            '& p': { margin: '0.5em 0' },
            '& h1, & h2, & h3': { margin: '0.75em 0 0.5em' },
            '& ul, & ol': { paddingLeft: '1.5em' },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: 'grey.300',
              paddingLeft: '1em',
              margin: '0.5em 0',
              fontStyle: 'italic',
            },
            '& a': { color: 'primary.main', textDecoration: 'underline' },
            '&:focus': {
              outline: 'none',
            },
          },
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 0 0 1px',
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  )
}

/**
 * Legacy render function for backward compatibility
 */
export const renderTextWidget = (textData: DashboardTextData | undefined): React.ReactNode => {
  return <RichTextRenderer textData={textData} />
}

/**
 * Legacy render editor function for backward compatibility
 */
export const renderTextWidgetEditor = (
  textData: DashboardTextData | undefined,
  onChange: (textData: DashboardTextData) => void
): React.ReactNode => {
  return <RichTextEditor textData={textData} onChange={onChange} />
}

export default RichTextEditor
